import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, AfterViewChecked, OnDestroy, AfterViewInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-chatbot',
    templateUrl: './chatbot.component.html',
    styleUrls: ['./chatbot.component.scss'],
    animations: [
        trigger('slideInOut', [
            state('open', style({
                transform: 'translateX(0)',
                opacity: 1
            })),
            state('closed', style({
                transform: 'translateX(100%)',
                opacity: 0
            })),
            transition('closed => open', [
                animate('300ms ease-in')
            ]),
            transition('open => closed', [
                animate('300ms ease-out')
            ])
        ])
    ],
    standalone: false
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnDestroy, AfterViewInit {
  private readonly API_URL = environment.chatbotApiUrl;
  private readonly API_TIMEOUT = 10000; // 10 seconds timeout for API calls

  showWelcome = true;
  welcomeMessage = 'Greetings! I\'m eXobe.AI. I\'m here to answer any questions you may have about shopping on EXOBE.';
  messages: { from: string; text: string; type: string; displayText?: string }[] = [];
  input = '';
  loading = false;
  sessionId: string;
  isChatbotOpen = true;
  isTyping = false;
  private typingInterval: any;
  private lastMessageCount = 0;
  private userScrolledUp = false;
  botAvatar: string | undefined;
  private saveTimeout: any; // For debouncing localStorage writes
  
  // Quick action buttons - will be populated with your content
  quickActions: Array<{ label: string; icon: string; isNew?: boolean }> = [];
  
  // Spotlight skills - will be populated with your content
  spotlightSkills: Array<{ text: string }> = [];

  @ViewChild('messageContainer') messageContainer: ElementRef;
  @ViewChild('inputRef') inputRef: ElementRef;
  @ViewChild('inputRefWelcome') inputRefWelcome: ElementRef;
  @Output() toggleChatbot = new EventEmitter<void>();

  constructor() {}

  ngAfterViewInit(): void {
    if (this.messageContainer && this.messageContainer.nativeElement) {
      const el: HTMLElement = this.messageContainer.nativeElement as HTMLElement;

      const wheelHandler = (evt: WheelEvent) => {
        const delta = evt.deltaY;
        const atTop = el.scrollTop === 0;
        const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
        if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
          evt.preventDefault();
        }
        evt.stopPropagation();
      };

      const touchHandler = (evt: TouchEvent) => {
        // Prevent body scroll when content can't scroll further
        evt.stopPropagation();
      };

      el.addEventListener('wheel', wheelHandler, { passive: false });
      el.addEventListener('touchmove', touchHandler, { passive: false });

      // Clean up on destroy
      const cleanup = () => {
        el.removeEventListener('wheel', wheelHandler as EventListener);
        el.removeEventListener('touchmove', touchHandler as EventListener);
      };
      (this as any)._chatbotScrollCleanup = cleanup;
    }
  }

  private generateSessionId(): string {
    return `session_${Math.random().toString(36).substring(2, 15)}`;
  }

  private saveMessagesToStorage(): void {
    // Debounce localStorage writes
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(() => {
      const maxMessages = 100;
      if (this.messages.length > maxMessages) {
        this.messages = this.messages.slice(-maxMessages);
      }
      const messagesToSave = this.messages.map(({ displayText, ...rest }) => rest);
      localStorage.setItem('chatbot_messages', JSON.stringify(messagesToSave));
    }, 500); // Wait 500ms before writing to localStorage
  }

  ngOnInit(): void {
    const isPageRefreshed = localStorage.getItem('pageRefreshed') === 'true';
    const welcomeShown = localStorage.getItem('chatbot_welcome_shown') === 'true';

    if (isPageRefreshed) {
      localStorage.removeItem('chatbot_messages');
      localStorage.removeItem('chatbot_session_id');
      localStorage.removeItem('chatbot_welcome_shown');
      localStorage.setItem('pageRefreshed', 'false');
    }

    this.sessionId = localStorage.getItem('chatbot_session_id') || this.generateSessionId();
    localStorage.setItem('chatbot_session_id', this.sessionId);

    if (welcomeShown) {
      this.showWelcome = false;
    }

    // Initialize quick actions - Replace with your content
    this.quickActions = [
      // Example: Add your quick action buttons here
      // { label: 'Baggage Allowance', icon: 'fa fa-suitcase', isNew: false },
      // { label: 'Booking', icon: 'fa fa-calendar', isNew: false },
      // { label: 'Check In', icon: 'fa fa-check-circle', isNew: true },
    ];

    // Initialize spotlight skills - Replace with your content
    this.spotlightSkills = [
      // Example: Add your spotlight skills here
      // { text: 'Get information on fare lock offering' },
    ];

    const storedMessages = localStorage.getItem('chatbot_messages');
    if (storedMessages && !isPageRefreshed) {
      this.messages = JSON.parse(storedMessages);
    } else {
      this.messages = [
        { from: 'bot', text: 'How can I help you today?', type: 'normal' }
      ];
      this.saveMessagesToStorage();
    }
    console.log('Initial messages:', this.messages);

    window.addEventListener('beforeunload', () => {
      localStorage.setItem('pageRefreshed', 'true');
    });
  }

  ngAfterViewChecked(): void {
    if (!this.showWelcome && this.messageContainer) {
      const container = this.messageContainer.nativeElement;

      if (this.messages.length !== this.lastMessageCount) {
        this.lastMessageCount = this.messages.length;
        this.scrollToBottom();
      }

      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
      if (!isAtBottom) {
        this.userScrolledUp = true;
      } else {
        this.userScrolledUp = false;
      }

      if (!this.userScrolledUp) {
        this.scrollToBottom();
      }

      console.log('Scrolled to bottom, scrollHeight:', container.scrollHeight, 'scrollTop:', container.scrollTop);
    }

    // Focus input when appropriate
    if (this.showWelcome && this.inputRefWelcome) {
      // Focus welcome input on initial load
      setTimeout(() => {
        if (this.inputRefWelcome && this.inputRefWelcome.nativeElement) {
          this.inputRefWelcome.nativeElement.focus();
        }
      }, 100);
    } else if (!this.showWelcome && this.inputRef && 
        (this.messages.length === 0 || this.messages[this.messages.length - 1].from === 'bot')) {
      this.inputRef.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
    }
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    if ((this as any)._chatbotScrollCleanup) {
      (this as any)._chatbotScrollCleanup();
    }
  }

  private scrollToBottom(): void {
    if (this.messageContainer) {
      const container = this.messageContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }

  private startTypingEffect(message: string, type: string): void {
    this.isTyping = true;
    const words = message.split(' ');
    let currentWordIndex = 0;
    const newMessage = { from: 'bot', text: message, type, displayText: '' };
    this.messages = [...this.messages, newMessage];

    this.typingInterval = setInterval(() => {
      if (currentWordIndex < words.length) {
        const currentMessage = this.messages[this.messages.length - 1];
        currentMessage.displayText = (currentMessage.displayText || '') + words[currentWordIndex] + ' ';
        this.messages = [...this.messages];
        currentWordIndex++;
      } else {
        clearInterval(this.typingInterval);
        this.isTyping = false;
        const currentMessage = this.messages[this.messages.length - 1];
        currentMessage.displayText = currentMessage.text;
        this.messages = [...this.messages];
        this.saveMessagesToStorage();
      }
    }, 50); // Reduced from 100ms to 50ms for faster typing effect
  }

  // Filter out "knowledge base" references from API responses
  private filterResponseText(responseText: string): string {
    return responseText.replace(/knowledge base/gi, 'guidance');
  }

  async handleSend(): Promise<void> {
    if (!this.input.trim()) return;

    // If on welcome screen, switch to chat interface when user sends first message
    if (this.showWelcome) {
      this.showWelcome = false;
      localStorage.setItem('chatbot_welcome_shown', 'true');
    }

    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.isTyping = false;
      if (this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1];
        lastMessage.displayText = lastMessage.text;
      }
    }

    const userMsg = { from: 'user', text: this.input, type: 'normal' };
    this.messages = [...this.messages, userMsg];
    this.saveMessagesToStorage();
    console.log('After adding user message:', this.messages);
    this.input = '';
    this.loading = true;

    try {
      console.log('Sending API request:', {
        url: this.API_URL,
        body: { prompt: userMsg.text, session_id: this.sessionId }
      });

      // Add timeout for API call
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.API_TIMEOUT);

      const response = await fetch(this.API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: userMsg.text,
          session_id: this.sessionId
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response:', data);

      if (data.session_id) {
        this.sessionId = data.session_id;
        localStorage.setItem('chatbot_session_id', this.sessionId);
        console.log('Updated sessionId:', this.sessionId);
      }

      let responseText = data.response || '';
      console.log('Extracted responseText:', responseText);

      // Filter out "knowledge base" references
      responseText = this.filterResponseText(responseText);

      this.loading = false;
      if (responseText) {
        this.startTypingEffect(responseText, 'success');
      } else {
        this.startTypingEffect('Sorry, I didn\'t get that.', 'error');
      }
    } catch (e) {
      console.error('Chat error:', {
        status: e.status,
        message: e.message
      });
      this.loading = false;
      if (e.name === 'AbortError') {
        this.startTypingEffect('The request took too long. Please try again.', 'error');
      } else {
        this.startTypingEffect(
          `Error communicating with the server: ${e.message || 'Unknown error'}.`,
          'error'
        );
      }
    }
  }

  handleInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.handleSend();
    }
  }
}